import React, { useEffect, useState, useMemo } from 'react';
import { Container, Card, Spinner, Button, Alert, Row, Col } from 'react-bootstrap';
import apiCall from '../Api/api';
import FeedCard from './FeedCard';
import PackageCard from './PackageCard';
import SharedSafariCard from './SharedSafariCard';
import SightingsUpdate from './SightingsUpdate';
import FeedCardSkeleton from './FeedCardSkeleton';
import PackageCardSkeleton from './PackageCardSkeleton';
import { Link } from 'react-router-dom';

const MainFeed = () => {
  const [rawFeeds, setRawFeeds] = useState([]);
  const [sightings, setSightings] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [summary, setSummary] = useState({ total_page: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canLoad, setCanLoad] = useState(true);

  const fetchFeeds = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall({
        url: `feeds?page=${pageIndex}&pageSize=5`,
        method: 'GET'
      });

      if (response?.status === 200 && response?.data?.data) {
        const fetchedFeeds = response.data.data.feeds || [];
        const totalPage = response.data.data.summary.total_page || 1;

        const flatFeeds = [];
        const newSightings = [];

        fetchedFeeds.forEach(feed => {
          if (feed.objective === 'sighting' && Array.isArray(feed.sighting_feeds)) {
            const validSightings = feed.sighting_feeds.map(s => s.feed).filter(Boolean);
            newSightings.push(...validSightings);
          } else {
            flatFeeds.push(feed);
          }
        });

        if (flatFeeds.length === 0 && newSightings.length === 0) {
          setCanLoad(false);
        }

        setRawFeeds(prev => [...prev, ...flatFeeds]);

        setSightings(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const unique = newSightings.filter(s => !existingIds.has(s.id));
          return [...prev, ...unique];
        });

        setSummary({ total_page: totalPage });
      } else {
        setError("Failed to fetch feed.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeeds();
  }, [pageIndex]);

  const processedFeed = useMemo(() => {
    const result = [];
    let sightingIndex = 0;
    const usedPackageIds = new Set();

    for (let i = 0; i < rawFeeds.length; i++) {
      const item = rawFeeds[i];

      if (item.objective === 'posts') {
        result.push({ type: 'posts', data: item });

        const nextPkg = rawFeeds.find(pkg =>
          pkg.objective === 'package' &&
          !usedPackageIds.has(pkg.package_feeds?.id)
        );

        if (nextPkg) {
          usedPackageIds.add(nextPkg.package_feeds?.id);
          result.push({ type: 'package', data: nextPkg.package_feeds });
        }
      } else if (item.objective === 'share_safari') {
        result.push({ type: 'shared-safari', data: item.feed });
      }

      if ((result.length > 0 && result.length % 5 === 0) && sightings.length > sightingIndex) {
        result.push({
          type: 'sightings',
          data: sightings.slice(sightingIndex, sightingIndex + 5),
        });
        sightingIndex += 5;
      }
    }

    return result;
  }, [rawFeeds, sightings]);

  const renderedFeedItems = processedFeed.map((item, idx) => {
    switch (item.type) {
      case 'posts':
        return <FeedCard key={`post-${idx}`} post={item.data.feed} />;

      case 'package':
        return (
          <div key={`pkg-carousel-${idx}`} className="mb-4">
            <Card className='px-3 py-3'>
              <div className="d-flex justify-content-between align-items-center px-2 px-md-3 mb-2">
                <h5 className="fw-bold text-success mb-0">Explore Wildlife Packages</h5>
                <Link to="/package" className="btn btn-sm rounded-pill btn-outline-success">
                  View All
                </Link>
              </div>
              <Row className="g-3">
                {item.data.slice(0, 2).map((pkgItem, i) => (
                  <Col key={`pkg-${idx}-${i}`} xs={12} md={6}>
                    <PackageCard pkg={pkgItem} />
                  </Col>
                ))}
              </Row>
            </Card>
          </div>
        );

      case 'shared-safari':
        return (
          <div className="mb-3 px-3" key={`safari-${idx}`}>
            <SharedSafariCard safari={item.data} />
          </div>
        );

      case 'sightings':
        return <SightingsUpdate key={`sightings-${idx}`} sightings={item.data} />;

      default:
        return null;
    }
  });

  const loadMore = () => {
    if (!canLoad || loading || pageIndex >= summary.total_page) return;

    setCanLoad(false);
    setPageIndex(prev => prev + 1);
    setTimeout(() => setCanLoad(true), 1000);
  };

  return (
    <Container className="pt-3">
      {error && <Alert variant="danger">{error}</Alert>}

      {processedFeed.length === 0 && loading && (
        <div className="px-3">
          {[...Array(2)].map((_, i) => (
            <FeedCardSkeleton key={`feed-skeleton-${i}`} />
          ))}

          <Card className="px-3 py-3 my-3">
            <div className="d-flex justify-content-between align-items-center px-2 px-md-3 mb-2">
              <h5 className="fw-bold text-success mb-0">Explore Wildlife Packages</h5>
              <div className="btn btn-sm rounded-pill btn-outline-success disabled">Loading</div>
            </div>
            <Row className="g-3">
              {[...Array(2)].map((_, i) => (
                <Col key={`pkg-skeleton-${i}`} xs={12} md={6}>
                  <PackageCardSkeleton />
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      )}

      {processedFeed.length === 0 && !loading && !error && (
        <div className="text-center text-muted my-5">
          No feeds available at the moment.
        </div>
      )}

      {renderedFeedItems}

      {loading && processedFeed.length > 0 && (
        <div className="text-center my-4">
          <Spinner animation="border" variant="success" />
        </div>
      )}

      {!loading && pageIndex < summary.total_page && canLoad && processedFeed.length > 0 && (
        <div className="text-center my-4">
          <Button
            onClick={loadMore}
            className="py-3 btn_newsafari organizeBtn newbg"
            style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}
            variant="success"
          >
            Load More
          </Button>
        </div>
      )}

      {!loading && (pageIndex >= summary.total_page || !canLoad) && processedFeed.length > 0 && (
        <div className="text-center text-muted my-3">
          You’ve reached the end of the feed.
        </div>
      )}
    </Container>
  );
};

export default MainFeed;
